import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import MaxWeightPreservedTypography from 'components/utils/MaxWeightPreservedTypography'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import type { InputHTMLAttributes, ReactElement, Ref } from 'react'
import React, { useContext } from 'react'
import styled from 'styled-components'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import BodyText from '../Typography/BodyText'
import SegmentedOptionsContext from './contexts/SegmentedOptionsContext'

const Title = styled(BodyTextBlock)`
  transition: color 200ms;
`

const Subtitle = styled(MaxWeightPreservedTypography)`
  transition: color 200ms;

  &:empty { display: none; }
`

export const _SegmentedOptionRoot = styled.label`
  transition: background-color 200ms, box-shadow 200ms;
  border-radius: ${props => props.theme.borderRadius.round};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${Title} { color: ${props => props.theme.palette.neutral.default.one}; }
  ${Subtitle} { color: ${props => props.theme.palette.neutral.default.three}; }

  &:has(input:checked) {
    ${Title} {
      color: ${props => props.theme.palette.neutral.default.eight};
      font-weight: ${props => props.theme.font.primary.weight.bold};
    }
    ${Subtitle} { color: ${props => props.theme.palette.neutral.default.five}; }
  }

  &:has(input:disabled) { 
    cursor: not-allowed;
    background-color: ${props => props.theme.palette.neutral.default.six};

    ${Title} { color: ${props => props.theme.palette.neutral.default.three}; }
    ${Subtitle} { color: ${props => props.theme.palette.neutral.default.three}; }
  }

  &:not(:has(input:disabled), :has(input:checked)) {
    cursor: pointer;

    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.brand.primary.lightest};

        ${Title} { color: ${props => props.theme.palette.neutral.default.one}; }
        ${Subtitle} { color: ${props => props.theme.palette.neutral.default.three}; }
      }
    }
  }

  &:not(:has(input:checked)):focus  {
    ${interactiveFocusStyle}
  }
`

type InheritedInputProps = Pick<InputHTMLAttributes<HTMLInputElement>, 'value' | 'checked' | 'defaultChecked' | 'required' | 'disabled' | 'onChange'>

interface Props extends InheritedInputProps {
  ref?: Ref<HTMLLabelElement>
  title: string | ReactElement
  subtitle?: string | ReactElement
  icon?: ReactElement | false
  inputRef?: Ref<HTMLInputElement>
  /**
   * @default SegmentedOptionsStateContext.inputsName
   */
  name?: string
}

function SegmentedOption({
  ref,
  title,
  subtitle,
  icon,
  inputRef,
  name,
  ...inputProps
}: Props) {
  const context = useContext(SegmentedOptionsContext)
  const inputName = name ?? context.inputsName

  return <_SegmentedOptionRoot ref={ref}>
    <Title variant="medium" startIcon={icon}>
      <MaxWeightPreservedTypography Typography={BodyText} variant="medium" align="center" wrap="truncate">
        {title}
      </MaxWeightPreservedTypography>
    </Title>
    {!!subtitle && <Subtitle Typography={BodyText} variant="small" align="center" wrap="truncate">
      {subtitle}
    </Subtitle>}
    <HiddenInput
      {...inputProps}
      name={inputName}
      ref={inputRef}
      type="radio"
    />
  </_SegmentedOptionRoot>
}

export default SegmentedOption
